import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateAlertText } from "../utils/boilerplate";
import { addAlert } from "./alert";

interface UserState {
  id: string;
  username: string;
  property: {
    id: string;
    name: string;
    currency: string;
    orange_limit: number;
    red_limit: number;
  };
  auth: {
    groups: [string, string];
    permissions: [string, string];
  };
  role: string;
  view: string[];
  language: string;
  timezone: [string, string];
  scope: Array<{ id: string; name: string }>;
}

// Define a type for the slice state
export interface AuthState {
  access: string | null;
  refresh: string | null;
  isAuthenticated: boolean;
  waiting: boolean;
  loading: boolean;
  user: UserState | null;
  email: {
    verification: {
      loading: boolean;
      sent: boolean;
    };
    referral: {
      loading: boolean;
      sent: boolean;
    };
    contact: {
      loading: boolean;
      sent: boolean;
    };
  };
}

// Define the initial state using that type
const initialState: AuthState = {
  access: localStorage.getItem("access"),
  refresh: localStorage.getItem("refresh"),
  isAuthenticated: false,
  waiting: false,
  loading: true,
  user: null,
  email: {
    verification: {
      loading: false,
      sent: false,
    },
    referral: {
      loading: false,
      sent: false,
    },
    contact: {
      loading: false,
      sent: false,
    },
  },
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get auth
export const getAuth = createAsyncThunk(
  "auth/get",
  async (arg, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Retrieve auth data
      let res = await axios.get(`/api/user`, config);
      if (res.status === 200) {
        return { data: res.data, status: res.status };
      }

      // If not successful, try to refresh credentials
      const refreshConfig = { headers: { "Content-Type": "application/json" } };
      const body = { refresh: localStorage.refresh };
      res = await axios.post(`/api/auth/login/refresh/`, body, refreshConfig);

      // Store new access token
      localStorage.setItem("access", res.data.access);

      // Try to get auth again
      const newConfig = {
        headers: { Authorization: `Bearer ${res.data.access}` },
      };
      res = await axios.get(`/api/user`, newConfig);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      // if (err.response && err.response.data.message) {
      //   return rejectWithValue(err.response.data.message);
      // } else {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Login
export const login = createAsyncThunk(
  "auth/post/login",
  async (
    obj: { username: string; password: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const { username, password } = obj;
      const config = { headers: { "Content-Type": "application/json" } };
      const body = JSON.stringify({ username, password });
      const res = await axios.post(`/api/auth/login/`, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Logout
export const logout = createAsyncThunk(
  "auth/post/logout",
  async (arg, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.access}`,
        },
      };
      const res = await axios.post(`/api/user/logout/`, null, config);

      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      return { data: res.data, status: res.status };
    } catch (err: any) {
      console.log(err);
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update Profile
export const updateProfile = createAsyncThunk(
  "auth/post/update-profile",
  async (
    obj: {
      language: string;
      username: string | undefined;
      currentPassword: string;
      password: string;
      confirmPassword: string;
      timezone: string | undefined;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const {
        language,
        username,
        currentPassword,
        password,
        confirmPassword,
        timezone,
      } = obj;

      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      let body: { [key: string]: any } = { language, username, timezone };
      if (password.length > 0) body.password = password;
      if (currentPassword.length > 0) body.current_password = currentPassword;
      if (confirmPassword.length > 0) body.confirm_password = confirmPassword;
      const jsonBody = JSON.stringify(body);

      const res = await axios.put(`/api/user/`, jsonBody, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      addAlert(generateAlertText("update_password_error", "UK"), "danger");
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update Language
export const updateLanguage = createAsyncThunk(
  "auth/post/update-language",
  async (
    obj: { username: string; language: string; newLanguage: string },
    { getState, rejectWithValue },
  ) => {
    const { username, newLanguage } = obj;
    const config = { headers: { "Content-Type": "application/json" } };
    const body = JSON.stringify({
      username,
      language: newLanguage,
    });

    try {
      const res = await axios.put(`/api/auth/profile/language/`, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      addAlert(generateAlertText("update_language_error", "UK"), "danger");
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Send contact us email
export const sendContactUsEmail = createAsyncThunk(
  "auth/post/contact-us/email",
  async (
    obj: {
      email: string;
      subject: string;
      message: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Configure authorization header with user's token
      const config = { headers: { "Content-Type": "application/json" } };

      const { email, subject, message } = obj;
      const body = JSON.stringify({ email, subject, message });

      const res = await axios.post(`/api/auth/contact/`, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get Auth
      .addCase(getAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAuth.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.loading = false;
        state.user = action.payload.data;
      })
      .addCase(getAuth.rejected, (state) => {
        state.loading = false;
      })

      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.access = action.payload.data.access;
        state.refresh = action.payload.data.refresh;
        state.isAuthenticated = true;

        // Set auth tokens in local storage
        localStorage.setItem("access", action.payload.data.access);
        localStorage.setItem("refresh", action.payload.data.refresh);
      })
      .addCase(login.rejected, (state) => {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        state.user = null;
        state.access = null;
        state.refresh = null;
        state.isAuthenticated = false;
        state.loading = false;
      })

      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.waiting = true;
      })
      .addCase(logout.fulfilled, (state) => {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        state.user = null;
        state.access = null;
        state.refresh = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(logout.rejected, (state) => {
        return state;
      })

      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload.data;
        state.loading = false;
      })
      .addCase(updateProfile.rejected, (state) => {
        state.loading = false;
      })

      // Update Language
      .addCase(updateLanguage.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateLanguage.fulfilled, (state, action) => {
        state.user = action.payload.data;
      })
      .addCase(updateLanguage.rejected, (state) => {
        return state;
      })

      // Send contact us email
      .addCase(sendContactUsEmail.pending, (state) => {
        state.email.contact.loading = true;
        state.email.contact.sent = false;
      })
      .addCase(sendContactUsEmail.fulfilled, (state, action) => {
        state.email.contact.loading = false;
        state.email.contact.sent = true;
      })
      .addCase(sendContactUsEmail.rejected, (state) => {
        state.email.contact.loading = false;
        state.email.contact.sent = false;
      });
  },
});

export default authSlice.reducer;
